import React from "react";

import { ChevronDown, KeyRound, LogOut, Moon, Plus, Sun } from "lucide-react";
import { toast } from "sonner";

import NotificationBell from "./NotificationBell";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";

/**
 * Standard header for feature pages.
 * @param {string} title - The page title
 * @param {string} actionLabel - Text for the primary action button
 * @param {function} onActionClick - Callback for button click
 * @param {string} subtitle - Optional descriptive text below title
 * @param {React.ComponentType} icon - Optional icon component (defaults to Plus)
 * @param {React.ReactNode} extraActions - Optional additional action components
 */
const PageHeader = ({
  title,
  subtitle,
  actionLabel,
  onActionClick,
  icon: ActionIcon = Plus,
  pageIcon: PageIcon,
  extraActions,
  notificationCount,
  showHeaderActions = true
}) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const api = useApi();
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = React.useState(false);
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [isUpdatingPassword, setIsUpdatingPassword] = React.useState(false);

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const handlePasswordUpdate = async () => {
    if (!newPassword.trim() || !confirmPassword.trim()) {
      toast.error("Please fill in all password fields");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (!user?.id) {
      toast.error("Unable to identify current user");
      return;
    }

    setIsUpdatingPassword(true);
    try {
      await api.put(`/users/${user.id}/password`, { newPassword });
      toast.success("Password updated successfully!");
      setIsPasswordDialogOpen(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.message || "Failed to update password");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <div className="space-y-4 mb-6 md:mb-8 animate-in fade-in slide-in-from-top-4 duration-500">
      <div className="flex items-start justify-between gap-4 min-w-0 w-full">
        <div className="flex items-center gap-3 md:gap-4 min-w-0">
          {PageIcon && (
            <div className="flex items-center justify-center h-10 w-10 md:h-14 md:w-14 rounded-xl md:rounded-2xl bg-primary/10 dark:bg-primary/20 text-primary shadow-sm border border-primary/10 dark:border-primary/20 transform transition-transform duration-300 hover:rotate-3 shrink-0">
              <PageIcon className="w-5 h-5 md:w-7 md:h-7" />
            </div>
          )}
          <div className="space-y-0.5 md:space-y-1 min-w-0">
            <h1 className="text-xl md:text-3xl font-extrabold text-yale-blue-900 dark:text-yale-blue-50 tracking-tight font-heading leading-tight truncate">
              {title}
            </h1>
            {subtitle && (
              <p className="text-yale-blue-500 dark:text-slate-400 font-medium text-[10px] md:text-sm max-w-lg truncate md:whitespace-normal">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {showHeaderActions && user && (
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <NotificationBell count={notificationCount} />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-10 px-2 rounded-md flex items-center gap-1 hover:bg-slate-100 dark:hover:bg-yale-blue-800"
                  aria-label="Profile menu"
                  title={user?.name || "Profile"}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.avatarUrl} />
                    <AvatarFallback className="bg-primary text-white text-xs font-bold">{getInitials(user?.name)}</AvatarFallback>
                  </Avatar>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer whitespace-nowrap">
                  {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  {theme === "dark" ? "Switch to Light Theme" : "Switch to Dark Theme"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIsPasswordDialogOpen(true)}
                  className="cursor-pointer whitespace-nowrap"
                >
                  <KeyRound className="h-4 w-4" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer font-semibold uppercase tracking-wider text-red-600 dark:text-red-400"
                >
                  <LogOut className="h-4 w-4" />
                  LOGOUT
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {(extraActions || actionLabel) && (
        <div className="flex items-center gap-3 md:gap-4 w-full">
          {extraActions && (
            <div className="flex items-center justify-end gap-3 flex-1 min-w-0 animate-in fade-in slide-in-from-top-2 duration-300">
              {extraActions}
            </div>
          )}

          {actionLabel && (
            <Button
              onClick={onActionClick}
              className={`group relative overflow-hidden bg-primary hover:bg-yale-blue-700 text-white font-bold h-11 w-11 md:w-auto md:px-6 rounded-xl shadow-lg shadow-primary/25 transition-all duration-300 active:scale-95 flex items-center justify-center gap-2 ${
                extraActions ? "shrink-0" : "ml-auto"
              }`}
            >
              <ActionIcon className="w-4 h-4 transition-transform group-hover:rotate-12" />
              <span className="hidden md:inline">{actionLabel}</span>
              <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            </Button>
          )}
        </div>
      )}

      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter and confirm your new password.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <Input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <Input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsPasswordDialogOpen(false);
                setNewPassword("");
                setConfirmPassword("");
              }}
            >
              Cancel
            </Button>
            <Button onClick={handlePasswordUpdate} disabled={isUpdatingPassword}>
              {isUpdatingPassword ? "Updating..." : "Update Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PageHeader;
